import { useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import OutboundIcon from '@mui/icons-material/OutboundOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import CorporateFareIcon from '@mui/icons-material/CorporateFareOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import NotificationBell from '@/components/common/NotificationBell';
import { getInitials } from '@/types/auth';
import { usePortalCategoryColors } from '@/pages/clientPortal/portalColors';
import portalLogo from '@/images/inventra_3pl.png';

const DRAWER_WIDTH = 248;

interface PortalNavItem {
  to: string;
  labelKey: string;
  icon: typeof DashboardIcon;
  colorKey: 'overview' | 'inventory' | 'grn' | 'dispatch' | 'invoices';
}

const NAV_ITEMS: PortalNavItem[] = [
  { to: '/portal/dashboard', labelKey: 'dashboard', icon: DashboardIcon, colorKey: 'overview' },
  { to: '/portal/inventory', labelKey: 'inventory', icon: Inventory2Icon, colorKey: 'inventory' },
  { to: '/portal/grn', labelKey: 'grn', icon: MoveToInboxIcon, colorKey: 'grn' },
  { to: '/portal/dispatch-orders', labelKey: 'dispatchOrders', icon: OutboundIcon, colorKey: 'dispatch' },
  { to: '/portal/invoices', labelKey: 'invoices', icon: ReceiptLongIcon, colorKey: 'invoices' },
];

export default function ClientPortalShell() {
  const { t } = useTranslation('clientPortal');
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const location = useLocation();
  const colors = usePortalCategoryColors();

  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);

  const isActive = (item: PortalNavItem) => location.pathname.startsWith(item.to);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
            backgroundImage: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(5,102,217,0.10) 0%, rgba(5,102,217,0) 220px)'
                : 'linear-gradient(180deg, rgba(5,102,217,0.06) 0%, rgba(5,102,217,0) 220px)',
          },
        }}
      >
        <Toolbar sx={{ px: 2.5, py: 3.5, justifyContent: 'center' }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#fff' : 'transparent'),
              borderRadius: (theme) => (theme.palette.mode === 'dark' ? 1.5 : 0),
              p: (theme) => (theme.palette.mode === 'dark' ? 1 : 0),
            }}
          >
            <Box component="img" src={portalLogo} alt="Inventra 3PL" sx={{ width: '100%', height: 'auto', display: 'block' }} />
          </Box>
        </Toolbar>

        <Box sx={{ px: 2, mb: 1.5 }}>
          <Divider />
        </Box>

        <List sx={{ px: 1.5, py: 0.5 }}>
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const selected = isActive(item);
            const accent = colors[item.colorKey];
            return (
              <ListItemButton
                key={item.to}
                component={RouterLink}
                to={item.to}
                selected={selected}
                sx={{
                  borderRadius: '10px',
                  mb: 0.75,
                  py: 1,
                  transition: 'background-color 0.15s ease',
                  '&.Mui-selected': {
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? `${accent}26` : `${accent}14`),
                    '&:hover': { bgcolor: (theme) => (theme.palette.mode === 'dark' ? `${accent}33` : `${accent}1f`) },
                  },
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '9px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: selected ? accent : 'action.selected',
                      color: selected ? '#fff' : 'text.secondary',
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <Icon fontSize="small" />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={t(item.labelKey)}
                  primaryTypographyProps={{ fontWeight: selected ? 700 : 500, color: selected ? accent : 'text.primary' }}
                />
                {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />}
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky" elevation={0}>
          <Toolbar sx={{ justifyContent: 'flex-end', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex' }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  minWidth: 0,
                  maxWidth: '100%',
                  pl: 0.75,
                  pr: 1.75,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(5, 102, 217, 0.16)' : 'rgba(5, 102, 217, 0.08)'),
                  border: '1px solid',
                  borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(5, 102, 217, 0.35)' : 'rgba(5, 102, 217, 0.18)'),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    flexShrink: 0,
                  }}
                >
                  <CorporateFareIcon sx={{ fontSize: 15 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ letterSpacing: 0.2, color: 'primary.main' }}>
                  {user?.companyName ?? t('clientPortal')}
                </Typography>
                {user?.clientName && (
                  <>
                    <ChevronRightIcon sx={{ fontSize: 16, color: 'primary.main', opacity: 0.5, flexShrink: 0 }} />
                    <Typography variant="subtitle1" fontWeight={800} noWrap>
                      {user.clientName}
                    </Typography>
                  </>
                )}
              </Stack>
            </Box>
            <NotificationBell />
            <Tooltip title={mode === 'dark' ? t('lightMode') : t('darkMode')}>
              <IconButton size="small" onClick={toggleMode}>
                {mode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <LanguageSwitcher />
            <Divider orientation="vertical" flexItem sx={{ my: 1.5, mx: 0.5 }} />
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              sx={{ cursor: 'pointer', borderRadius: '2px', px: 1, py: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Avatar src={user?.avatarUrl ?? undefined} sx={{ width: 32, height: 32, bgcolor: user?.avatarColor, fontSize: 14 }}>
                {user ? getInitials(user) : undefined}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {user?.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('clientPortal')}
                </Typography>
              </Box>
            </Stack>
            <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  void logout();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t('logout')}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
